///
/// EarthBrowser
///
/// Created by Matt Giger on December 24, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { VectorTile } from '@mapbox/vector-tile'
import Protobuf from 'pbf'
import { type VectorTileId } from './VectorTileId'
import type {
	DecodedVectorTile,
	ToGeoJSONOptions,
	VectorTileFeature,
	VectorTileFeatureGeoJSON,
	VectorTileLayer
} from './types'

/**
 * MVTDecoder - Decodes MapLibre/Mapbox Vector Tile (MVT) Protocol Buffer format
 *
 * Features:
 * - Decode MVT PBF data into structured format
 * - Convert to GeoJSON
 * - Extract layer and feature information
 * - Handle compressed and uncompressed tiles
 */
export class MVTDecoder {

	/**
	 * Decode a vector tile from raw PBF data
	 */
	public static decode(data: ArrayBuffer, tileId: VectorTileId): DecodedVectorTile {
		try {
			const pbf = new Protobuf(data)
			const vt = new VectorTile(pbf)

			const layers = new Map<string, VectorTileLayer>()

			// Process each layer
			for (const layerName in vt.layers) {
				const layer = vt.layers[layerName]
				const features: VectorTileFeature[] = []

				// Extract all features from this layer
				for (let i = 0; i < layer.length; i++) {
					const feature = layer.feature(i)

					features.push({
						id: feature.id,
						type: feature.type,
						properties: feature.properties,
						geometry: feature.loadGeometry(),
						extent: layer.extent
					})
				}

				// keys/values exist at runtime but are missing from the library typings
				const rawLayer = layer as unknown as { keys?: string[], values?: unknown[] }
				layers.set(layerName, {
					name: layerName,
					version: layer.version,
					extent: layer.extent,
					features,
					keys: rawLayer.keys ?? [],
					values: rawLayer.values ?? []
				})
			}

			return {
				tileId,
				layers,
				rawData: data
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new Error(`Failed to decode vector tile ${tileId.key}: ${error.message}`)
			}
			throw new Error(`Failed to decode vector tile ${tileId.key}: Unknown error`)
		}
	}

	/**
	 * Check if data appears to be a valid MVT tile
	 */
	public static isValidMVT(data: ArrayBuffer): boolean {
		try {
			// Check for gzip header
			const bytes = new Uint8Array(data)
			if (bytes.length < 2) return false

			// Check if gzipped (shouldn't be for our use case)
			if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
				return false
			}

			// Try to parse as protobuf
			const pbf = new Protobuf(data)
			new VectorTile(pbf)

			return true
		} catch {
			return false
		}
	}

	/**
	 * Get layer names from a tile without full decoding
	 */
	public static getLayerNames(data: ArrayBuffer): string[] {
		try {
			const pbf = new Protobuf(data)
			const vt = new VectorTile(pbf)
			return Object.keys(vt.layers)
		} catch {
			return []
		}
	}

	/**
	 * Get basic info about a tile without full decoding
	 */
	public static getTileInfo(data: ArrayBuffer, tileId: VectorTileId): {
		tileId: VectorTileId
		layers: Array<{ name: string, featureCount: number, extent: number }>
		totalFeatures: number
		size: number
	} {
		const pbf = new Protobuf(data)
		const vt = new VectorTile(pbf)

		const layers: Array<{ name: string, featureCount: number, extent: number }> = []
		let totalFeatures = 0

		for (const layerName in vt.layers) {
			const layer = vt.layers[layerName]
			const featureCount = layer.length
			totalFeatures += featureCount

			layers.push({
				name: layerName,
				featureCount,
				extent: layer.extent
			})
		}

		return {
			tileId,
			layers,
			totalFeatures,
			size: data.byteLength
		}
	}

	/**
	 * Convert a decoded tile to GeoJSON FeatureCollection
	 */
	public static toGeoJSON(
		decoded: DecodedVectorTile,
		options?: ToGeoJSONOptions
	): {
		type: 'FeatureCollection'
		features: VectorTileFeatureGeoJSON[]
	} {
		const features: VectorTileFeatureGeoJSON[] = []
		const [west, south, east, north] = decoded.tileId.bounds

		// Process each layer
		for (const [layerName, layer] of decoded.layers) {
			// Filter by layer if specified
			if (options?.layers && !options.layers.includes(layerName)) {
				continue
			}

			// Process each feature in the layer
			for (const feature of layer.features) {
				// Filter by properties if specified
				if (options?.propertyFilter && !options.propertyFilter(feature.properties)) {
					continue
				}

				const geoJsonFeature = MVTDecoder.featureToGeoJSON(
					feature,
					layer.extent,
					[west, south, east, north],
					options?.coordinateTransform
				)

				if (geoJsonFeature) {
					features.push(geoJsonFeature)
				}
			}
		}

		return {
			type: 'FeatureCollection',
			features
		}
	}

	/**
	 * Convert a single feature to GeoJSON
	 */
	private static featureToGeoJSON(
		_feature: VectorTileFeature,
		_extent: number,
		_bounds: [number, number, number, number],
		_coordinateTransform?: (x: number, y: number) => [number, number]
	): VectorTileFeatureGeoJSON | null {

		return null

		// const [west, south, east, north] = bounds
		// const xScale = (east - west) / extent
		// const yScale = (north - south) / extent

		// const projectPoint = (x: number, y: number): [number, number] => {
		// 	let lng = west + x * xScale
		// 	let lat = north - y * yScale

		// 	if (coordinateTransform) {
		// 		return coordinateTransform(lng, lat)
		// 	}

		// 	return [lng, lat]
		// }

		// const projectRing = (ring: number[][]): number[][] => {
		// 	return ring.map(([x, y]) => projectPoint(x, y))
		// }

		// try {
		// 	switch (feature.type) {
		// 		case 1: { // POINT
		// 			if (feature.geometry.length === 0 || feature.geometry[0].length === 0) return null

		// 			const coords = feature.geometry[0][0]
		// 			const [lng, lat] = projectPoint(coords[0], coords[1])

		// 			if (feature.geometry.length === 1 && feature.geometry[0].length === 1) {
		// 				// Single point
		// 				return {
		// 					type: 'Feature',
		// 					id: feature.id,
		// 					properties: feature.properties,
		// 					geometry: {
		// 						type: 'Point',
		// 						coordinates: [lng, lat]
		// 					}
		// 				}
		// 			} else {
		// 				// MultiPoint
		// 				const points = feature.geometry.flat().map(([x, y]) => projectPoint(x, y))
		// 				return {
		// 					type: 'Feature',
		// 					id: feature.id,
		// 					properties: feature.properties,
		// 					geometry: {
		// 						type: 'MultiPoint',
		// 						coordinates: points
		// 					}
		// 				}
		// 			}
		// 		}

		// 		case 2: { // LINESTRING
		// 			if (feature.geometry.length === 0) return null

		// 			if (feature.geometry.length === 1) {
		// 				// Single LineString
		// 				return {
		// 					type: 'Feature',
		// 					id: feature.id,
		// 					properties: feature.properties,
		// 					geometry: {
		// 						type: 'LineString',
		// 						coordinates: projectRing(feature.geometry[0])
		// 					}
		// 				}
		// 			} else {
		// 				// MultiLineString
		// 				return {
		// 					type: 'Feature',
		// 					id: feature.id,
		// 					properties: feature.properties,
		// 					geometry: {
		// 						type: 'MultiLineString',
		// 						coordinates: feature.geometry.map(ring => projectRing(ring))
		// 					}
		// 				}
		// 			}
		// 		}

		// 		case 3: { // POLYGON
		// 			if (feature.geometry.length === 0) return null

		// 			// Group rings into polygons (exterior + holes)
		// 			const polygons: number[][][][] = []
		// 			let currentPolygon: number[][][] = []

		// 			for (const ring of feature.geometry) {
		// 				const projectedRing = projectRing(ring)
		// 				const area = MVTDecoder.signedArea(projectedRing)

		// 				if (area > 0) {
		// 					// Exterior ring - start new polygon
		// 					if (currentPolygon.length > 0) {
		// 						polygons.push(currentPolygon)
		// 					}
		// 					currentPolygon = [projectedRing]
		// 				} else {
		// 					// Hole ring - add to current polygon
		// 					if (currentPolygon.length > 0) {
		// 						currentPolygon.push(projectedRing)
		// 					}
		// 				}
		// 			}

		// 			if (currentPolygon.length > 0) {
		// 				polygons.push(currentPolygon)
		// 			}

		// 			if (polygons.length === 0) return null

		// 			if (polygons.length === 1) {
		// 				// Single Polygon
		// 				return {
		// 					type: 'Feature',
		// 					id: feature.id,
		// 					properties: feature.properties,
		// 					geometry: {
		// 						type: 'Polygon',
		// 						coordinates: polygons[0]
		// 					}
		// 				}
		// 			} else {
		// 				// MultiPolygon
		// 				return {
		// 					type: 'Feature',
		// 					id: feature.id,
		// 					properties: feature.properties,
		// 					geometry: {
		// 						type: 'MultiPolygon',
		// 						coordinates: polygons
		// 					}
		// 				}
		// 			}
		// 		}

		// 		default:
		// 			return null
		// 	}
		// } catch (error) {
		// 	console.warn('Failed to convert feature to GeoJSON:', error)
		// 	return null
		// }
	}

	/**
	 * Calculate signed area of a ring (for determining exterior vs hole)
	 * Currently unused while featureToGeoJSON is disabled
	 */
	// private static signedArea(ring: number[][]): number {
	// 	let area = 0
	// 	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
	// 		const [xi, yi] = ring[i]
	// 		const [xj, yj] = ring[j]
	// 		area += (xj - xi) * (yi + yj)
	// 	}
	// 	return area
	// }

	/**
	 * Extract properties from all features in a layer
	 */
	public static getLayerProperties(decoded: DecodedVectorTile, layerName: string): Set<string> {
		const layer = decoded.layers.get(layerName)
		if (!layer) return new Set()

		const props = new Set<string>()
		for (const feature of layer.features) {
			Object.keys(feature.properties).forEach(key => props.add(key))
		}

		return props
	}

	/**
	 * Get unique values for a property across all features in a layer
	 */
	public static getPropertyValues(
		decoded: DecodedVectorTile,
		layerName: string,
		propertyName: string
	): Set<unknown> {
		const layer = decoded.layers.get(layerName)
		if (!layer) return new Set()

		const values = new Set<unknown>()
		for (const feature of layer.features) {
			const value = feature.properties[propertyName]
			if (value !== undefined) {
				values.add(value)
			}
		}

		return values
	}
}

